// Página móvil-first para que el supervisor revise y dé VB a partes diarios.
// Diseño: lista compacta con cards grandes (touch-friendly) que el supervisor
// pueda usar desde el celular en obra. Cada card abre un modal con detalle
// del parte + input PIN + botón "Confirmar VB".
import { useState, useEffect, useCallback } from 'react';
import { partesVbApi } from '../lib/api';
import { useRegisterCmdKCommands } from '../contexts/CmdKContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Loader2, ShieldCheck, Clock, CheckCircle2, KeyRound, ClipboardCheck, AlertCircle, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { PartePreviewDialog } from '../components/PartePreviewDialog';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

export default function MisVBPage() {
  const [tab, setTab] = useState('pendientes');
  const [pendientes, setPendientes] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pinStatus, setPinStatus] = useState({ configured: false, supervisor_nombre: '' });
  const [openParte, setOpenParte] = useState(null);
  const [pinInput, setPinInput] = useState('');
  const [comentario, setComentario] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pinSetupOpen, setPinSetupOpen] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [newPinConfirm, setNewPinConfirm] = useState('');
  const [previewParteId, setPreviewParteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pend, hist, pst] = await Promise.all([
        partesVbApi.misPendientes(),
        partesVbApi.misHistorial(50),
        partesVbApi.pinStatus(),
      ]);
      setPendientes(pend.data?.items || []);
      setHistorial(hist.data?.items || []);
      setPinStatus(pst.data || { configured: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando partes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Cmd+K: comandos contextuales
  useRegisterCmdKCommands(
    'mis-vb',
    'Mis VB Supervisor',
    [
      {
        id: 'vb-refresh',
        label: 'Refrescar lista de partes',
        icon: 'RefreshCw',
        keywords: 'refrescar recargar actualizar',
        run: () => load(),
      },
      {
        id: 'vb-next-pending',
        label: `Revisar siguiente parte pendiente${pendientes.length ? ` (${pendientes.length} pendientes)` : ''}`,
        sublabel: pendientes[0] ? `${pendientes[0].fecha} · OT ${pendientes[0].ot_principal || '-'}` : 'No hay pendientes',
        icon: 'ArrowRight',
        keywords: 'siguiente proximo pendiente revisar abrir',
        run: () => {
          if (pendientes.length > 0) {
            setTab('pendientes');
            openParteModal(pendientes[0]);
          }
        },
        enabled: pendientes.length > 0,
      },
      {
        id: 'vb-tab-pendientes',
        label: 'Ir a tab Pendientes',
        icon: 'Clock',
        run: () => setTab('pendientes'),
        enabled: tab !== 'pendientes',
      },
      {
        id: 'vb-tab-historial',
        label: 'Ir a tab Historial',
        icon: 'CheckCircle',
        run: () => setTab('historial'),
        enabled: tab !== 'historial',
      },
      {
        id: 'vb-pin-setup',
        label: pinStatus.configured ? 'Cambiar PIN VB' : 'Configurar PIN VB',
        sublabel: pinStatus.configured ? 'Actualizar tu PIN personal' : 'Necesario para dar VB',
        icon: 'Settings',
        keywords: 'pin configurar cambiar setup',
        run: () => setPinSetupOpen(true),
      },
    ],
    [pendientes, tab, pinStatus.configured, load],
  );

  const openParteModal = (parte) => {
    setOpenParte(parte);
    setPinInput('');
    setComentario('');
  };

  const submitVB = async () => {
    if (!pinInput || pinInput.length < 4) {
      toast.error('PIN inválido (mínimo 4 dígitos)');
      return;
    }
    setSubmitting(true);
    try {
      await partesVbApi.darVB(openParte.id, pinInput, comentario);
      toast.success('VB registrado correctamente');
      setOpenParte(null);
      setPinInput('');
      setComentario('');
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Error al registrar VB';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const submitPinSetup = async () => {
    if (!newPin || newPin.length < 4 || !/^\d+$/.test(newPin)) {
      toast.error('PIN debe ser 4-8 dígitos');
      return;
    }
    if (newPin !== newPinConfirm) {
      toast.error('Los PIN no coinciden');
      return;
    }
    try {
      await partesVbApi.setPin(newPin);
      toast.success('PIN configurado correctamente');
      setPinSetupOpen(false);
      setNewPin('');
      setNewPinConfirm('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al configurar PIN');
    }
  };

  return (
    <div className="container max-w-3xl mx-auto p-3 md:p-6 space-y-4" data-testid="page-mis-vb">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-emerald-600" />
            Mis VB
          </h1>
          <p className="text-sm text-muted-foreground">
            Visto Bueno de partes diarios como supervisor
            {pinStatus.supervisor_nombre && (
              <> · <span className="font-medium text-foreground">{pinStatus.supervisor_nombre}</span></>
            )}
          </p>
        </div>
        <Button
          variant={pinStatus.configured ? 'outline' : 'default'}
          size="sm"
          onClick={() => setPinSetupOpen(true)}
          data-testid="btn-pin-setup"
        >
          <KeyRound className="w-4 h-4 mr-1" />
          {pinStatus.configured ? 'Cambiar PIN VB' : 'Configurar PIN VB'}
        </Button>
      </div>

      {!pinStatus.configured && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/30">
          <CardContent className="py-3 flex items-start gap-2 text-sm">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-200">Sin PIN configurado</p>
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Para dar VB necesitás configurar un PIN personal. Esto previene que alguien con el ERP abierto en tu computador apruebe partes en tu nombre.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="pendientes" data-testid="tab-pendientes">
            <Clock className="w-4 h-4 mr-1" />
            Pendientes
            {pendientes.length > 0 && (
              <Badge className="ml-2 bg-amber-500" data-testid="badge-pendientes-count">{pendientes.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="historial" data-testid="tab-historial">
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Historial
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pendientes" className="space-y-2 mt-3">
          {loading && (
            <div className="text-center py-8 text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Cargando…
            </div>
          )}
          {!loading && pendientes.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                <ClipboardCheck className="w-10 h-10 mx-auto mb-2 opacity-40" />
                No tenés partes pendientes de VB.
              </CardContent>
            </Card>
          )}
          {pendientes.map((p) => (
            <Card
              key={p.id}
              className="cursor-pointer hover:border-emerald-400 transition active:scale-[0.99] touch-manipulation"
              onClick={() => openParteModal(p)}
              data-testid={`card-parte-pendiente-${p.id}`}
            >
              <CardHeader className="p-3 pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <span>{p.fecha}</span>
                  <Badge variant="outline" className="text-xs">{p.personal_count} pers.</Badge>
                </CardTitle>
                <CardDescription className="text-xs">
                  OT <strong className="text-foreground">{p.ot_principal || '-'}</strong>
                  {p.prevencionista_nombre && <> · Preparado por: {p.prevencionista_nombre}</>}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-3 pt-0 flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={(e) => { e.stopPropagation(); setPreviewParteId(p.id); }}
                  data-testid={`btn-preview-parte-${p.id}`}
                >
                  <Eye className="w-4 h-4 mr-1" /> Vista previa
                </Button>
                <Button size="sm" className="flex-1" data-testid={`btn-ver-parte-${p.id}`}>
                  <ShieldCheck className="w-4 h-4 mr-1" /> Revisar y dar VB
                </Button>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="historial" className="space-y-2 mt-3">
          {historial.length === 0 && !loading && (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Sin historial de VB todavía.
              </CardContent>
            </Card>
          )}
          {historial.map((p) => (
            <Card key={p.id} data-testid={`card-parte-historial-${p.id}`}>
              <CardHeader className="p-3 pb-1">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span>{p.fecha} · OT {p.ot_principal || '-'}</span>
                  <Badge className="bg-emerald-500"><CheckCircle2 className="w-3 h-3 mr-1" /> VB dado</Badge>
                </CardTitle>
                <CardDescription className="text-xs">
                  Dado el {fmtDate(p.vb_fecha)} · {p.personal_count} personas
                  {p.vb_comentario && <><br /><em>"{p.vb_comentario}"</em></>}
                </CardDescription>
              </CardHeader>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Modal: revisar parte y dar VB */}
      <Dialog open={!!openParte} onOpenChange={(o) => !o && setOpenParte(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-dar-vb">
          {openParte && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                  Visto Bueno
                </DialogTitle>
                <DialogDescription>
                  Parte del <strong>{openParte.fecha}</strong> · OT {openParte.ot_principal || '-'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="bg-slate-50 dark:bg-slate-900 border rounded p-3 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-muted-foreground">Prevencionista:</span><span>{openParte.prevencionista_nombre || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Personal:</span><span className="font-medium">{openParte.personal_count}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Estado:</span><span><Badge variant="outline">{openParte.estado}</Badge></span></div>
                  {openParte.fecha_cierre && (
                    <div className="flex justify-between"><span className="text-muted-foreground">Cerrado:</span><span>{fmtDate(openParte.fecha_cierre)}</span></div>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setPreviewParteId(openParte.id)}
                  data-testid="btn-preview-desde-modal"
                >
                  <Eye className="w-4 h-4 mr-1" /> Ver detalle del parte ({openParte.personal_count} personas)
                </Button>
                <div className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded p-2">
                  Verificá que el horario y la asignación de HH son correctos.
                  Tu PIN registra el VB de manera personal — no compartir.
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">PIN VB</label>
                  <Input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={8}
                    value={pinInput}
                    onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
                    placeholder="••••"
                    autoComplete="off"
                    data-testid="input-vb-pin"
                    className="text-2xl tracking-widest text-center font-mono"
                  />
                  {!pinStatus.configured && (
                    <p className="text-xs text-red-600 mt-1">
                      Aún no configuraste tu PIN. <button className="underline" onClick={() => { setOpenParte(null); setPinSetupOpen(true); }}>Configurar ahora</button>
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Comentario (opcional)</label>
                  <Textarea
                    rows={2}
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                    placeholder="Observaciones sobre el parte…"
                    maxLength={500}
                    data-testid="input-vb-comentario"
                  />
                </div>
              </div>
              <DialogFooter className="gap-2 flex-col sm:flex-row">
                <Button variant="outline" onClick={() => setOpenParte(null)} disabled={submitting}>Cancelar</Button>
                <Button
                  onClick={submitVB}
                  disabled={submitting || !pinInput || !pinStatus.configured}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="btn-confirmar-vb"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                  Confirmar VB
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal: vista previa del parte */}
      <PartePreviewDialog
        parteId={previewParteId}
        open={!!previewParteId}
        onOpenChange={(o) => !o && setPreviewParteId(null)}
      />

      {/* Modal: configurar/cambiar PIN */}
      <Dialog open={pinSetupOpen} onOpenChange={setPinSetupOpen}>
        <DialogContent className="max-w-sm" data-testid="dialog-pin-setup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-emerald-600" />
              {pinStatus.configured ? 'Cambiar PIN VB' : 'Configurar PIN VB'}
            </DialogTitle>
            <DialogDescription>4-8 dígitos. Solo vos lo conocés.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Nuevo PIN</label>
              <Input
                type="password" inputMode="numeric" maxLength={8}
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                className="text-xl text-center tracking-widest font-mono"
                data-testid="input-new-pin"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Confirmar PIN</label>
              <Input
                type="password" inputMode="numeric" maxLength={8}
                value={newPinConfirm}
                onChange={(e) => setNewPinConfirm(e.target.value.replace(/\D/g, ''))}
                className="text-xl text-center tracking-widest font-mono"
                data-testid="input-new-pin-confirm"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPinSetupOpen(false)}>Cancelar</Button>
            <Button onClick={submitPinSetup} disabled={!newPin || newPin !== newPinConfirm} data-testid="btn-save-pin">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
